import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';

/**
 * Authentication service base URL from environment
 */
const AUTH_BASE_URL = (window as any).__ENV_CONFIG__?.AUTH_URL || process.env.REACT_APP_AUTHENTICATION_URL || '';

/**
 * User session data from auth-service
 */
export interface UserSession {
    authenticated: boolean;
    loginRequired?: boolean;
    loginUrl?: string;
    userId?: number;
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    authorities?: string[];
    ecmUser?: boolean;
    ecmRole?: string;
    spectreUser?: boolean;
    sessionCreatedAt?: string;
}

/**
 * Whether the user may open the ECM application.
 *
 * <p>Access is decided solely by {@code ecmUser}: the ECM role carried on the session describes
 * what the user may do inside ECM and is deliberately not consulted here, so it never influences
 * the landing page or any redirect.</p>
 *
 * Accepts a loosely typed session because single-spa route guards read it off
 * {@code window.__USER_SESSION__}, which is unset until the first session check completes.
 */
export const canAccessEcm = (session: UserSession | null | undefined): boolean => {
    return !!session?.ecmUser;
};

/**
 * Whether the user may open the Spectre application.
 *
 * Accepts a loosely typed session for the same reason as {@link canAccessEcm}.
 */
export const canAccessSpectre = (session: UserSession | null | undefined): boolean => {
    return !!session?.spectreUser;
};

/**
 * Route prefixes owned by the registered microfrontends. The prefix match must stay identical to
 * the one used by the single-spa `activeWhen` guards, otherwise a path could mount a microfrontend
 * that the shell does not police (or the other way round).
 */
export const ECM_ROUTE_PREFIX = '/ecm';
export const SPECTRE_ROUTE_PREFIX = '/spectre';

export const isEcmRoute = (pathname: string): boolean => pathname.startsWith(ECM_ROUTE_PREFIX);
export const isSpectreRoute = (pathname: string): boolean => pathname.startsWith(SPECTRE_ROUTE_PREFIX);

/**
 * Whether the given path is reachable with the entitlements carried on the session.
 * Paths outside the microfrontend prefixes (i.e. the shell's own landing page) are always allowed.
 */
export const isPathAllowed = (pathname: string, session: UserSession | null | undefined): boolean => {
    if (isEcmRoute(pathname)) {
        return canAccessEcm(session);
    }
    if (isSpectreRoute(pathname)) {
        return canAccessSpectre(session);
    }
    return true;
};

/**
 * The path the user should land on.
 *
 * <p>A user entitled to exactly one application is sent straight into it, because the landing page
 * would only ever show that single tile. Users with both (or neither) entitlement stay on the
 * landing page, which renders the tiles or the access-denied panel respectively.</p>
 */
export const resolveLandingPath = (session: UserSession | null | undefined): string => {
    const ecm = canAccessEcm(session);
    const spectre = canAccessSpectre(session);
    if (ecm && !spectre) {
        return ECM_ROUTE_PREFIX;
    }
    if (spectre && !ecm) {
        return SPECTRE_ROUTE_PREFIX;
    }
    return '/';
};

/**
 * Auth context value type
 */
interface AuthContextValue {
    session: UserSession | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    sessionExpired: boolean;
    loginFailed: boolean;
    logout: () => Promise<void>;
    checkSession: () => Promise<void>;
    handleSessionExpiredLogin: () => void;
    handleLoginFailureRetry: () => void;
    clearLoginFailure: () => void;
}

/**
 * Default context value
 */
const defaultContextValue: AuthContextValue = {
    session: null,
    isLoading: true,
    isAuthenticated: false,
    sessionExpired: false,
    loginFailed: false,
    logout: async () => { },
    checkSession: async () => { },
    handleSessionExpiredLogin: () => { },
    handleLoginFailureRetry: () => { },
    clearLoginFailure: () => { }
};

/**
 * Auth context for sharing authentication state across components
 */
export const AuthContext = createContext<AuthContextValue>(defaultContextValue);

/**
 * Hook to access auth context
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/**
 * Auth provider props
 */
interface AuthProviderProps {
    children: ReactNode;
}

/**
 * Backoff for retrying a session check that could not reach the auth service.
 */
const MAX_TRANSIENT_RETRIES = 5;
const RETRY_BASE_DELAY_MS = 2000;
const RETRY_MAX_DELAY_MS = 10000;

/**
 * Heartbeat interval. Each successful call slides the Redis session TTL and the session cookie,
 * so it must stay comfortably shorter than the server-side session timeout (120 minutes).
 */
const HEARTBEAT_INTERVAL_MS = 20 * 60 * 1000;

/**
 * Auth provider component that manages authentication state.
 * After initial login, monitors session health via:
 * - Visibility change (tab re-focus): immediate check, then the heartbeat resumes
 * - Periodic heartbeat (every 20 minutes) while the tab is visible
 * The heartbeat is suspended while the tab is hidden, so a user who leaves the application for
 * longer than the server-side session timeout comes back to the session-expired dialog.
 * Shows a session-expired dialog only when the auth service explicitly answers 401.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [session, setSession] = useState<UserSession | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [loginFailed, setLoginFailed] = useState(false);
    // Track if redirect is in progress to prevent loops
    const isRedirecting = useRef(false);
    // Track if session has been checked to prevent duplicate calls (e.g., React StrictMode)
    const hasCheckedSession = useRef(false);
    // Track if the user was previously authenticated (to distinguish initial login from expiry)
    const wasAuthenticated = useRef(false);
    // Retry bookkeeping for transient failures (network blip, auth-service rollout)
    const transientRetryCount = useRef(0);
    const retryTimer = useRef<number | null>(null);
    // Heartbeat timer, only running while the tab is visible
    const heartbeatTimer = useRef<number | null>(null);
    // Lets checkSession schedule a retry of itself without a circular dependency
    const checkSessionRef = useRef<((isBackgroundCheck: boolean) => Promise<void>) | undefined>(undefined);

    /**
     * Handle a session check that could not be completed.
     * The session is only considered gone when the auth service explicitly answers 401, so a
     * transient failure keeps the current session and schedules another attempt.
     */
    const handleTransientFailure = useCallback((reason: string) => {
        console.warn(`Session check could not complete (${reason}); keeping the current session`);

        if (transientRetryCount.current >= MAX_TRANSIENT_RETRIES) {
            console.warn('Giving up on session check retries until the next heartbeat');
            return;
        }

        const attempt = ++transientRetryCount.current;
        const delay = Math.min(RETRY_BASE_DELAY_MS * attempt, RETRY_MAX_DELAY_MS);
        if (retryTimer.current !== null) {
            window.clearTimeout(retryTimer.current);
        }
        retryTimer.current = window.setTimeout(() => {
            retryTimer.current = null;
            // Background check once authenticated so the UI keeps rendering the current session
            void checkSessionRef.current?.(wasAuthenticated.current);
        }, delay);
    }, []);

    /**
     * Check session with auth-service.
     * @param isBackgroundCheck - if true, this is a periodic/visibility check (not initial load)
     */
    const checkSession = useCallback(async (isBackgroundCheck: boolean = false) => {
        try {
            if (!isBackgroundCheck) {
                setIsLoading(true);
            }
            const response = await fetch(`${AUTH_BASE_URL}/session`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (response.status !== 401 && !response.ok) {
                // 5xx/503 from the auth service or the ingress. The Redis session is untouched,
                // so the user stays signed in and we simply try again shortly.
                handleTransientFailure(`HTTP ${response.status}`);
                return;
            }

            if (response.status === 401) {
                if (wasAuthenticated.current) {
                    // User WAS authenticated but session expired → show dialog
                    setSession({ authenticated: false });
                    (window as any).__USER_SESSION__ = null;
                    setSessionExpired(true);
                    return;
                }
                // Not authenticated on initial load — redirect to login immediately
                if (!isRedirecting.current) {
                    // Detect redirect loops: if we redirected very recently, don't redirect again
                    const lastRedirect = sessionStorage.getItem('auth_redirect_ts');
                    const now = Date.now();
                    if (lastRedirect && now - parseInt(lastRedirect, 10) < 5000) {
                        console.error('Auth redirect loop detected — not redirecting again');
                        setSession({ authenticated: false });
                        return;
                    }
                    isRedirecting.current = true;
                    sessionStorage.setItem('auth_redirect_ts', now.toString());
                    // Try to extract loginUrl from response body, fall back to /login
                    let loginPath = '/login';
                    try {
                        const data: UserSession = await response.json();
                        if (data.loginUrl) {
                            loginPath = data.loginUrl.replace('/api/auth', '');
                        }
                    } catch (e) {
                        // Response may not be valid JSON — use default login path
                    }
                    const currentPath = window.location.pathname + window.location.search;
                    const loginUrl = `${AUTH_BASE_URL}${loginPath}?redirectUrl=${encodeURIComponent(currentPath)}`;
                    window.location.href = loginUrl;
                    return;
                }
                setSession({ authenticated: false });
            } else if (response.ok) {
                const data: UserSession = await response.json();
                // Clear any redirect-loop marker on successful auth
                sessionStorage.removeItem('auth_redirect_ts');
                transientRetryCount.current = 0;
                // Store session data on window for single-spa route guards
                (window as any).__USER_SESSION__ = data;
                setSession(data);
                wasAuthenticated.current = true;
                setSessionExpired(false);
                // Trigger single-spa to re-evaluate active applications.
                // This is needed when the page loads directly on a microfrontend route
                // (e.g. full page reload or new tab), because single-spa's start() runs
                // synchronously before the async session fetch completes, so activeWhen
                // initially returns false. Dispatching popstate forces a re-check.
                if (!isBackgroundCheck) {
                    window.dispatchEvent(new PopStateEvent('popstate'));
                }
            }
        } catch (error) {
            // A failed fetch says nothing about the server-side session: it lives in Redis and is
            // only gone once the auth service actually answers 401. Never sign the user out here.
            console.error('Session check failed:', error);
            handleTransientFailure('network error');
        } finally {
            // Keep loading state while redirecting to SSO to prevent landing page flash
            if (!isRedirecting.current) {
                setIsLoading(false);
            }
        }
    }, [handleTransientFailure]);

    /**
     * Handle login redirect from the session-expired dialog
     */
    const handleSessionExpiredLogin = useCallback(() => {
        const currentPath = window.location.pathname + window.location.search;
        const loginUrl = `${AUTH_BASE_URL}/login?redirectUrl=${encodeURIComponent(currentPath)}`;
        window.location.href = loginUrl;
    }, []);

    /**
     * Handle retry from login failure dialog
     */
    const handleLoginFailureRetry = useCallback(() => {
        setLoginFailed(false);
        const currentPath = window.location.pathname + window.location.search;
        const loginUrl = `${AUTH_BASE_URL}/login?redirectUrl=${encodeURIComponent(currentPath)}`;
        window.location.href = loginUrl;
    }, []);

    /**
     * Clear login failure state
     */
    const clearLoginFailure = useCallback(() => {
        setLoginFailed(false);
    }, []);

    /**
     * Logout - calls auth-service logout and clears session
     */
    const logout = async () => {
        let sloUrl: string | null = null;
        try {
            const response = await fetch(`${AUTH_BASE_URL}/logout`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                sloUrl = data.sloUrl || null;
            }
        } catch (error) {
            console.error('Logout failed:', error);
        }

        // Clear local state regardless of backend response
        setSession({ authenticated: false });
        (window as any).__USER_SESSION__ = null;
        wasAuthenticated.current = false;

        if (sloUrl) {
            // Redirect to IdP Single Logout to terminate the SSO session
            // (same pattern as ecm-service's OidcLogoutSuccessHandler)
            window.location.href = sloUrl;
        } else {
            // No SLO URL configured — show session-expired dialog
            setSessionExpired(true);
        }
    };

    // Check for login error in URL on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const loginError = urlParams.get('loginError');
        if (loginError === 'true') {
            setLoginFailed(true);
            // Clean up URL without reloading
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        }
    }, []);

    // Keep the ref pointing at the latest checkSession for scheduled retries
    useEffect(() => {
        checkSessionRef.current = checkSession;
    }, [checkSession]);

    // Cancel any pending retry when the provider unmounts
    useEffect(() => {
        return () => {
            if (retryTimer.current !== null) {
                window.clearTimeout(retryTimer.current);
            }
        };
    }, []);

    // Check session on mount
    useEffect(() => {
        // Prevent duplicate calls (e.g., React StrictMode double-mounting)
        if (hasCheckedSession.current) {
            return;
        }
        hasCheckedSession.current = true;
        checkSession(false);
    }, [checkSession]);

    // Heartbeat while authenticated and the tab is in the foreground. Each call slides the Redis
    // TTL and refreshes the access token server-side, so an active user is never logged out.
    // The heartbeat is stopped whenever the tab is hidden and restarted (after an immediate check)
    // when the user comes back, so idling away from the application lets the session lapse.
    useEffect(() => {
        const isAuthenticated = session?.authenticated ?? false;

        const stopHeartbeat = () => {
            if (heartbeatTimer.current !== null) {
                window.clearInterval(heartbeatTimer.current);
                heartbeatTimer.current = null;
            }
        };

        const startHeartbeat = () => {
            stopHeartbeat();
            heartbeatTimer.current = window.setInterval(() => {
                if (document.visibilityState === 'visible') {
                    void checkSession(true);
                }
            }, HEARTBEAT_INTERVAL_MS);
        };

        if (!isAuthenticated || sessionExpired) {
            stopHeartbeat();
            return;
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Returning to the application: verify the session right away, then resume polling
                void checkSession(true);
                startHeartbeat();
                return;
            }
            // Moving to another tab: stop calling the heartbeat until the user returns
            stopHeartbeat();
            if (retryTimer.current !== null) {
                window.clearTimeout(retryTimer.current);
                retryTimer.current = null;
            }
        };

        if (document.visibilityState === 'visible') {
            startHeartbeat();
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            stopHeartbeat();
        };
    }, [checkSession, sessionExpired, session]);

    const value: AuthContextValue = {
        session,
        isLoading,
        isAuthenticated: session?.authenticated ?? false,
        sessionExpired,
        loginFailed,
        logout,
        checkSession: () => checkSession(false),
        handleSessionExpiredLogin,
        handleLoginFailureRetry,
        clearLoginFailure
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
